import { Image } from 'expo-image';
import React, { useMemo } from 'react';
import { ImageBackground, Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Car } from '@/api/cars/types';
import {
  formatNumber,
  getCarBrandLogo,
  getCarImages,
  getColorImgSrc,
  isDesktop,
} from '@/core';
import { PINKLING } from '@/style/colors';
import { Card, Text } from '@/ui';

import { Chip } from '../chip';
import { Icon } from '../icon';
import { Battery } from '../icons/battery';
import { Location } from '../icons/location';
import { Seat } from '../icons/seat';
import { SteeringWheel } from '../icons/steering-wheel';

interface CarCardProps {
  car: Car;
  onPress: () => void;
  stacked?: boolean;
}

export default function CarCard({
  car,
  onPress,
  stacked = false,
}: CarCardProps) {
  const { styles, theme } = useStyles(stylesheet);

  const colorImgSrc = useMemo(() => {
    return getColorImgSrc(car.color);
  }, [car.color]);

  const interiorColorImgSrc = useMemo(() => {
    return getColorImgSrc(car.interiorColor);
  }, [car.interiorColor]);

  return (
    <Card style={stacked ? undefined : { flexGrow: 1 }}>
      <Pressable
        style={[styles.container, stacked && styles.stackedContainer]}
        onPress={onPress}
      >
        <ImageBackground
          source={{
            uri:
              car && getCarImages(car.images).length > 0
                ? getCarImages(car.images)[0].url
                : '',
          }}
          resizeMode="contain"
          style={[styles.imageBackground, stacked && styles.stackedImage]}
          imageStyle={{
            width: '100%',
            borderRadius: 16,
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'space-between',
              padding: 16,
            }}
          >
            <View>
              <Text
                type="caption2"
                tx="carCard.milesFromAddress"
                values={{
                  miles: formatNumber(
                    car.deliveryDistance !== -1
                      ? car.deliveryDistance
                      : car.distanceFromAddress
                  ),
                }}
              />
            </View>

            {car.supportsDelivery ? (
              <View
                style={{
                  alignSelf: 'flex-start',
                  flexDirection: 'row',
                  gap: 6,
                  alignItems: 'center',
                }}
              >
                <Icon
                  type="naked"
                  icon={<Location />}
                  fillColor={theme.colors.chip.brand.color}
                  stroke={theme.colors.chip.brand.color}
                  width={12}
                  height={12}
                />

                <Text
                  tx="carCard.deliveryAvailable"
                  style={{ color: PINKLING }}
                  type="caption2"
                />
              </View>
            ) : null}
          </View>
        </ImageBackground>

        <View
          style={[
            {
              paddingLeft: stacked || !isDesktop() ? 8 : 0,
              paddingRight: 8,
              paddingBottom: 8,
              paddingTop: isDesktop() ? 8 : 0,
              justifyContent: stacked ? 'flex-start' : 'space-between',
              flexGrow: stacked ? 0 : 1,
              gap: stacked ? 12 : 0,
            },
          ]}
        >
          <View style={styles.headerRow}>
            <View style={styles.identity}>
              <View style={styles.brandMark}>
                <Image
                  style={{
                    width: 26,
                    height: 26,
                  }}
                  source={getCarBrandLogo(car.brandLogos)}
                  contentFit="cover"
                />
              </View>

              <View style={{ flexShrink: 1 }}>
                <Text type="bodyBold">{`${car.brandName} ${car.modelName}`}</Text>
                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {car.modelType}
                </Text>
              </View>
            </View>

            <View>
              <View style={{ flexDirection: 'row', gap: 4 }}>
                {car.discountedDailyRate ? (
                  <Text
                    type="bodyRegular"
                    style={{ textDecorationLine: 'line-through' }}
                  >
                    {`$${car.dailyRate}`}
                  </Text>
                ) : null}

                <Text type="bodyBold">{`$${
                  formatNumber(car.discountedDailyRate) ||
                  formatNumber(car.dailyRate)
                } / day`}</Text>
              </View>

              <View style={{ alignSelf: 'flex-end' }}>
                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {`Total $${formatNumber(car.totalRate)}`}
                </Text>
              </View>
            </View>
          </View>

          {stacked ? (
            <View style={{ gap: 8 }}>
              <View style={styles.stackedSpecRow}>
                <Chip
                  style={styles.stackedChip}
                  icon={<SteeringWheel />}
                  iconWidth={12}
                  iconHeight={12}
                  text={car.autopilotType}
                />

                <Chip
                  style={styles.stackedChip}
                  icon={<Battery />}
                  iconWidth={12}
                  iconHeight={12}
                  text={`${car.batteryRange} miles`}
                />

                <Chip
                  style={styles.stackedChip}
                  icon={<Seat />}
                  iconWidth={12}
                  iconHeight={12}
                  text={`${car.numSeats} seats`}
                />
              </View>

              <View style={styles.stackedSpecRow}>
                <Chip
                  style={styles.stackedChip}
                  imgSrc={colorImgSrc}
                  iconWidth={12}
                  iconHeight={12}
                  useDefaultIconFillColor={false}
                  text="Exterior"
                />

                {interiorColorImgSrc ? (
                  <Chip
                    style={styles.stackedChip}
                    imgSrc={interiorColorImgSrc}
                    iconWidth={12}
                    iconHeight={12}
                    useDefaultIconFillColor={false}
                    text="Interior"
                  />
                ) : null}
              </View>
            </View>
          ) : (
            <View style={{ gap: 8 }}>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <Chip icon={<SteeringWheel />} text={car.autopilotType} />

                <Chip
                  icon={<Battery />}
                  text={`${car.batteryRange} Miles Per Charge`}
                />
              </View>

              <View style={{ flexDirection: 'row', gap: 8 }}>
                <Chip icon={<Seat />} text={`${car.numSeats} Seats`} />

                <Chip
                  imgSrc={colorImgSrc}
                  useDefaultIconFillColor={false}
                  text="Exterior"
                />
              </View>
            </View>
          )}
        </View>
      </Pressable>
    </Card>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    padding: 8,
    gap: 16,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    height: {
      xs: 370,
      md: 220,
    },
  },
  imageBackground: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: theme.colors.background_1,
    borderRadius: 16,
    width: {
      xs: '100%',
      md: '50%',
    },
    height: {
      xs: 200,
      md: '100%',
    },
  },
  stackedContainer: {
    flexDirection: 'column',
    height: 'auto',
    gap: 8,
  },
  stackedImage: {
    width: '100%',
    height: 160,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  identity: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 1,
  },
  brandMark: {
    backgroundColor: theme.colors.icon.fill,
    padding: 10,
    borderRadius: 8,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stackedSpecRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  stackedChip: {
    paddingVertical: 4,
    paddingHorizontal: 6,
    gap: 4,
  },
}));
